import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { RouteFormData, Source, Location } from "./types/route"
import { useRouteForm } from "./hooks/useRouteForm"
import SourceSelector from "./components/routes/SourceSelector"
import LocationSelector from "./components/routes/LocationSelector"
import {
  FaSave,
  FaTimes,
  FaMapMarkerAlt,
  FaBuilding,
  FaSpinner,
  FaInfoCircle,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa"
import Swal from "sweetalert2"

interface RouteFormPageProps {
  routeId?: string
}

const RouteFormPage: React.FC<RouteFormPageProps> = ({ routeId }) => {
  const navigate = useNavigate()
  const { loading, saveRoute, loadRoute } = useRouteForm()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RouteFormData>({
    defaultValues: {
      id: "",
      status: false,
      source: {
        type: "Source",
        data: { id: "", name: "", alias: "" },
      },
      target: {
        type: "Location",
        data: { criteria0: "", criteria1: "", criteria2: "", criteria3: "" },
      },
    },
  })

  const watchedValues = watch()

  // Load route data for editing
  useEffect(() => {
    if (routeId) {
      loadRoute(routeId).then((route) => {
        if (route) {
          reset({
            id: route.id,
            status: route.status,
            source: route.source,
            target: route.target,
          })
        }
      })
    }
  }, [routeId, loadRoute, reset])

  const handleSourceTypeChange = (type: "Source" | "Location") => {
    setValue("source.type", type)
    if (type === "Source") {
      setValue("source.data", { id: "", name: "", alias: "" })
    } else {
      setValue("source.data", { criteria0: "", criteria1: "", criteria2: "", criteria3: "" })
    }
  }

  const handleTargetTypeChange = (type: "Source" | "Location") => {
    setValue("target.type", type)
    if (type === "Source") {
      setValue("target.data", { id: "", name: "", alias: "" })
    } else {
      setValue("target.data", { criteria0: "", criteria1: "", criteria2: "", criteria3: "" })
    }
  }

  const onSubmit = async (data: RouteFormData) => {
    const success: any = await saveRoute(data)

    if (success) {
      Swal.fire({
        title: "Hecho",
        text: "Ruta guardada correctamente",
        icon: "success",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#002EFE",
      })
      navigate("/routes")
    } else {
      Swal.fire({
        title: "Error",
        text: "No se pudo guardar la ruta. Por favor, inténtelo de nuevo.",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#d33",
      })
    }
  }

  const handleCancel = () => {
    navigate("/routes")
  }

  const getSourceFieldError = (field: string) => {
    return errors.source?.data?.[field as keyof (Source | Location)]
  }

  const getTargetFieldError = (field: string) => {
    return errors.target?.data?.[field as keyof (Source | Location)]
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* General Information Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FaInfoCircle className="w-5 h-5 text-gray-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Información general</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ID de ruta <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("id", {
                    required: "El ID de la ruta es obligatorio",
                    minLength: { value: 1, message: "El ID de la ruta no puede estar vacío" },
                  })}
                  type="text"
                  placeholder="Ingrese el ID de la ruta"
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.id ? "border-red-300" : "border-gray-300"
                  }`}
                />
                {errors.id && (
                  <div className="text-sm text-red-600 flex items-center space-x-1 mt-1">
                    <span>{errors.id.message}</span>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Identificador único para esta ruta</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => setValue("status", !watchedValues?.status)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                      watchedValues?.status
                        ? "bg-green-50 border-green-200 text-green-700"
                        : "bg-red-50 border-red-200 text-red-700"
                    }`}
                  >
                    {watchedValues?.status ? (
                      <>
                        <FaToggleOn className="w-5 h-5 text-green-600" />
                        <span className="font-medium">Activo</span>
                      </>
                    ) : (
                      <>
                        <FaToggleOff className="w-5 h-5 text-red-600" />
                        <span className="font-medium">Inactivo</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Haga clic para cambiar el estado de la ruta</p>
              </div>
            </div>
          </div>

          {/* Source Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FaBuilding className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Configuración de origen</h3>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Tipo de origen</label>
              <div className="flex space-x-6">
                <label className="flex items-center">
                  <input
                    {...register("source.type")}
                    type="radio"
                    value="Source"
                    checked={watchedValues?.source?.type === "Source"}
                    onChange={() => handleSourceTypeChange("Source")}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Source</span>
                </label>
                <label className="flex items-center">
                  <input
                    {...register("source.type")}
                    type="radio"
                    value="Location"
                    checked={watchedValues?.source?.type === "Location"}
                    onChange={() => handleSourceTypeChange("Location")}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Location</span>
                </label>
              </div>
            </div>

            {watchedValues?.source?.type === "Source" ? (
              <SourceSelector
                register={register}
                setValue={setValue}
                watch={watch}
                error={getSourceFieldError("id") || getSourceFieldError("name")}
                label="Sources"
                fieldPath="source.data"
              />
            ) : (
              <LocationSelector
                register={register}
                setValue={setValue}
                watch={watch}
                error={getSourceFieldError("criteria0")}
                label="Locations"
                fieldPath="source.data"
              />
            )}
          </div>

          {/* Target Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-100 rounded-lg">
                <FaMapMarkerAlt className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Configuración de destino</h3>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Tipo de destino</label>
              <div className="flex space-x-6">
                <label className="flex items-center">
                  <input
                    {...register("target.type")}
                    type="radio"
                    value="Source"
                    checked={watchedValues?.target?.type === "Source"}
                    onChange={() => handleTargetTypeChange("Source")}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Source</span>
                </label>
                <label className="flex items-center">
                  <input
                    {...register("target.type")}
                    type="radio"
                    value="Location"
                    checked={watchedValues?.target?.type === "Location"}
                    onChange={() => handleTargetTypeChange("Location")}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Location</span>
                </label>
              </div>
            </div>

            {watchedValues?.target?.type === "Source" ? (
              <SourceSelector
                register={register}
                setValue={setValue}
                watch={watch}
                error={getTargetFieldError("id") || getTargetFieldError("name")}
                label="Sources"
                fieldPath="target.data"
              />
            ) : (
              <LocationSelector
                register={register}
                setValue={setValue}
                watch={watch}
                error={getTargetFieldError("criteria0")}
                label="Locations"
                fieldPath="target.data"
              />
            )}
          </div>

          {/* Form Validation Errors */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
              <h4 className="text-sm font-semibold text-red-700 mb-2">Errores de validación</h4>
              <ul className="list-disc list-inside space-y-1">
                {Object.entries(errors).map(([key, error]) => (
                  <li key={key} className="text-sm text-red-600">
                    {error.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <FaTimes className="w-4 h-4" />
              <span>Cancelar</span>
            </button>
            <button
              type="submit"
              disabled={loading || isSubmitting}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading || isSubmitting ? (
                <FaSpinner className="w-4 h-4 animate-spin" />
              ) : (
                <FaSave className="w-4 h-4" />
              )}
              <span>{loading || isSubmitting ? "Guardando..." : "Guardar ruta"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default RouteFormPage
